import React, { useState, useMemo } from "react";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  Plus,
  Trash2,
  Check,
  X,
  User,
} from "lucide-react";
import { theme } from "../../../shared/styles/theme";
import { useAuth } from "../../auth/context/AuthContext";
import {
  useAgenda,
  DiaExtraAgenda,
  ConfigAgendaProfissional,
} from "../context/AgendaContext";
import { useUsuarios } from "../../usuarios/context/UsuariosContext";
import { CATEGORIA_LABELS } from "../../../types";

const DIAS_SEMANA = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

interface ProgramacaoMensalProps {
  profissionalIdInicial?: string;
}

export default function ProgramacaoMensal({
  profissionalIdInicial,
}: ProgramacaoMensalProps) {
  const { usuarioLogado } = useAuth();
  const {
    configsProfissionais,
    diasExtras,
    adicionarDiaExtra,
    excluirDiaExtra,
    getDiasExtrasProfissional,
    agendamentosPorData,
  } = useAgenda();
  const { usuarios, carregarUsuarios } = useUsuarios();

  React.useEffect(() => {
    if (usuarios.length === 0) carregarUsuarios();
  }, []);

  const [profissionalId, setProfissionalId] = useState(
    profissionalIdInicial || ""
  );
  const [mesAno, setMesAno] = useState(() => {
    const hoje = new Date();
    return { mes: hoje.getMonth(), ano: hoje.getFullYear() };
  });
  const [horaInicio, setHoraInicio] = useState("08:00");
  const [horaFim, setHoraFim] = useState("12:00");

  // Profissionais com agenda configurada
  const profissionaisConfigurados = useMemo(() => {
    return configsProfissionais
      .filter((c) => c.ativo)
      .map((c) => ({
        ...c,
        usuario: usuarios.find((u: any) => u.id === c.profissionalId),
      }));
  }, [configsProfissionais, usuarios]);

  const configSelecionado = useMemo(() => {
    return configsProfissionais.find(
      (c) => c.profissionalId === profissionalId
    );
  }, [profissionalId, configsProfissionais]);

  // Dias extras do profissional neste mês
  const mesStr = `${mesAno.ano}-${String(mesAno.mes + 1).padStart(2, "0")}`;
  const diasExtrasDoMes = useMemo(() => {
    if (!profissionalId) return [];
    return getDiasExtrasProfissional(profissionalId, mesStr);
  }, [profissionalId, mesStr, diasExtras]);

  // Gerar dias do mês para o calendário
  const diasMes = useMemo(() => {
    const primeiroDia = new Date(mesAno.ano, mesAno.mes, 1);
    const ultimoDia = new Date(mesAno.ano, mesAno.mes + 1, 0);
    const diasNoMes = ultimoDia.getDate();
    const inicioSemana = primeiroDia.getDay();

    const dias: { data: string; dia: number; foraDoMes: boolean }[] = [];

    for (let i = inicioSemana - 1; i >= 0; i--) {
      const dd = new Date(mesAno.ano, mesAno.mes, -i);
      dias.push({
        data: `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(dd.getDate()).padStart(2, "0")}`,
        dia: dd.getDate(),
        foraDoMes: true,
      });
    }

    for (let i = 1; i <= diasNoMes; i++) {
      dias.push({
        data: `${mesAno.ano}-${String(mesAno.mes + 1).padStart(
          2,
          "0"
        )}-${String(i).padStart(2, "0")}`,
        dia: i,
        foraDoMes: false,
      });
    }

    while (dias.length % 7 !== 0) {
      const dd = new Date(
        mesAno.ano,
        mesAno.mes + 1,
        dias.length - diasNoMes - inicioSemana + 1
      );
      dias.push({
        data: `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(dd.getDate()).padStart(2, "0")}`,
        dia: dd.getDate(),
        foraDoMes: true,
      });
    }

    return dias;
  }, [mesAno]);

  // Verificar se um dia é fixo (agenda fixa)
  const isDiaFixo = (data: string): boolean => {
    if (!configSelecionado || configSelecionado.tipoAgenda !== "fixa")
      return false;
    const diaSemana = new Date(data + "T00:00:00").getDay();
    return (configSelecionado.diasFixos || []).some(
      (d) => d.diaSemana === diaSemana
    );
  };

  // Verificar se um dia tem extra aberto
  const getDiaExtra = (data: string): DiaExtraAgenda | undefined => {
    return diasExtrasDoMes.find((d) => d.data === data);
  };

  // Verificar agendamentos no dia
  const getAgendamentosNoDia = (data: string) => {
    return agendamentosPorData(data).filter(
      (a) => a.profissionalId === profissionalId
    );
  };

  // Navegação
  const navegar = (dir: number) => {
    setMesAno((prev) => {
      let novoMes = prev.mes + dir;
      let novoAno = prev.ano;
      if (novoMes > 11) {
        novoMes = 0;
        novoAno++;
      }
      if (novoMes < 0) {
        novoMes = 11;
        novoAno--;
      }
      return { mes: novoMes, ano: novoAno };
    });
  };

  // Clicar num dia para abrir/fechar
  const toggleDia = async (data: string) => {
    if (!profissionalId || !configSelecionado) return;
    const hoje = new Date().toISOString().split("T")[0];
    if (data < hoje) return; // Não permite abrir dias passados

    const dataBR = data.split("-").reverse().join("/");
    const diaSemNome = [
      "Domingo",
      "Segunda",
      "Terça",
      "Quarta",
      "Quinta",
      "Sexta",
      "Sábado",
    ][new Date(data + "T00:00:00").getDay()];

    // Se é dia fixo, não pode remover (é automático)
    if (isDiaFixo(data)) {
      // Mas pode adicionar período extra neste dia
      const extra = getDiaExtra(data);
      if (extra) {
        // Verificar se tem agendamentos
        const ag = getAgendamentosNoDia(data);
        if (ag.length > 0) {
          alert(
            `Não é possível remover: existem ${ag.length} agendamento(s) neste dia.`
          );
          return;
        }
        if (
          !window.confirm(
            `Deseja remover o período extra de ${diaSemNome} ${dataBR} (${extra.horaInicio}-${extra.horaFim})?`
          )
        )
          return;
        await excluirDiaExtra(extra.id);
      } else {
        // Validar que o horário extra não sobreponha com o período fixo
        const diaSemana = new Date(data + "T00:00:00").getDay();
        const periodosFixos =
          configSelecionado.diasFixos?.filter(
            (d) => d.diaSemana === diaSemana
          ) || [];

        for (const pf of periodosFixos) {
          if (horaInicio < pf.horaFim && horaFim > pf.horaInicio) {
            alert(
              `Conflito de horário! Este dia já tem atendimento fixo das ${pf.horaInicio} às ${pf.horaFim}.\n\n` +
                `O período extra (${horaInicio}-${horaFim}) sobrepõe com o fixo.\n\n` +
                `Ajuste o horário início/fim antes de clicar no dia.`
            );
            return;
          }
        }

        if (
          !window.confirm(
            `Deseja abrir período extra em ${diaSemNome} ${dataBR} (${horaInicio}-${horaFim})?`
          )
        )
          return;
        await adicionarDiaExtra({
          profissionalId,
          profissionalNome: configSelecionado.profissionalNome,
          data,
          horaInicio,
          horaFim,
          criadoPor: usuarioLogado?.id || "",
          criadoPorNome: usuarioLogado?.nome || "",
        });
      }
      return;
    }
    // Para agenda livre ou dia não-fixo
    const extra = getDiaExtra(data);
    if (extra) {
      // Verificar se tem agendamentos
      const ag = getAgendamentosNoDia(data);
      if (ag.length > 0) {
        alert(
          `Não é possível fechar este dia: existem ${ag.length} agendamento(s). Cancele-os primeiro.`
        );
        return;
      }
      if (
        !window.confirm(
          `Deseja cancelar o atendimento de ${diaSemNome} ${dataBR} (${extra.horaInicio}-${extra.horaFim})?`
        )
      )
        return;
      await excluirDiaExtra(extra.id);
    } else {
      if (
        !window.confirm(
          `Deseja abrir atendimento em ${diaSemNome} ${dataBR} (${horaInicio}-${horaFim})?`
        )
      )
        return;
      await adicionarDiaExtra({
        profissionalId,
        profissionalNome: configSelecionado.profissionalNome,
        data,
        horaInicio,
        horaFim,
        criadoPor: usuarioLogado?.id || "",
        criadoPorNome: usuarioLogado?.nome || "",
      });
    }
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {/* SELETOR DE PROFISSIONAL */}
      <div
        style={{
          display: "flex",
          gap: "12px",
          alignItems: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: "200px" }}>
          <label
            style={{
              fontSize: "11px",
              fontWeight: "600",
              color: theme.gray[500],
              fontFamily: theme.fonts.sans,
            }}
          >
            Profissional
          </label>
          <select
            value={profissionalId}
            onChange={(e) => setProfissionalId(e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              border: `1px solid ${theme.gray[300]}`,
              borderRadius: theme.borderRadius.sm,
              fontSize: "13px",
              fontFamily: theme.fonts.sans,
            }}
          >
            <option value="">Selecione um profissional...</option>
            {profissionaisConfigurados.map((c) => (
              <option key={c.profissionalId} value={c.profissionalId}>
                {c.profissionalNome} —{" "}
                {CATEGORIA_LABELS[
                  c.profissionalCategoria as keyof typeof CATEGORIA_LABELS
                ] || c.profissionalCategoria}
                {c.tipoAgenda === "fixa" ? " (Fixa)" : " (Livre)"}
              </option>
            ))}
          </select>
        </div>
        {profissionalId && (
          <>
            <div>
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: theme.gray[500],
                  fontFamily: theme.fonts.sans,
                }}
              >
                Horário início
              </label>
              <input
                type="time"
                value={horaInicio}
                onChange={(e) => setHoraInicio(e.target.value)}
                style={{
                  padding: "8px",
                  border: `1px solid ${theme.gray[300]}`,
                  borderRadius: theme.borderRadius.sm,
                  fontSize: "13px",
                }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: "11px",
                  fontWeight: "600",
                  color: theme.gray[500],
                  fontFamily: theme.fonts.sans,
                }}
              >
                Horário fim
              </label>
              <input
                type="time"
                value={horaFim}
                onChange={(e) => setHoraFim(e.target.value)}
                style={{
                  padding: "8px",
                  border: `1px solid ${theme.gray[300]}`,
                  borderRadius: theme.borderRadius.sm,
                  fontSize: "13px",
                }}
              />
            </div>
          </>
        )}
      </div>

      {/* LEGENDA */}
      {profissionalId && (
        <div
          style={{
            display: "flex",
            gap: "16px",
            fontSize: "10px",
            fontFamily: theme.fonts.sans,
            color: theme.gray[500],
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <div
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "3px",
                background: "#3b82f6",
              }}
            />
            Dia fixo (automático)
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <div
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "3px",
                background: "#22c55e",
              }}
            />
            Dia aberto manualmente
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <div
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "3px",
                background: "#f59e0b",
              }}
            />
            Dia fixo + extra
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <div
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "3px",
                background: theme.gray[200],
              }}
            />
            Sem atendimento
          </span>
          <span style={{ fontStyle: "italic" }}>
            Clique no dia para abrir/fechar
          </span>
        </div>
      )}

      {profissionalId && (
        <>
          {/* NAVEGAÇÃO MÊS */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 12px",
              background: theme.gray[50],
              borderRadius: theme.borderRadius.md,
            }}
          >
            <button
              onClick={() => navegar(-1)}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: theme.gray[600],
              }}
            >
              <ChevronLeft size={18} />
            </button>
            <span
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: theme.gray[800],
                fontFamily: theme.fonts.sans,
              }}
            >
              {MESES[mesAno.mes]} {mesAno.ano}
            </span>
            <button
              onClick={() => navegar(1)}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: theme.gray[600],
              }}
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* CALENDÁRIO */}
          <div>
            {/* CABEÇALHO */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: "2px",
                marginBottom: "4px",
              }}
            >
              {DIAS_SEMANA.map((d) => (
                <div
                  key={d}
                  style={{
                    textAlign: "center",
                    fontSize: "11px",
                    fontWeight: "700",
                    color: theme.gray[500],
                    fontFamily: theme.fonts.sans,
                    padding: "4px",
                  }}
                >
                  {d}
                </div>
              ))}
            </div>
            {/* GRID */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: "2px",
              }}
            >
              {diasMes.map((dia, i) => {
                const hoje = new Date().toISOString().split("T")[0];
                const isPassado = dia.data < hoje;
                const isHoje = dia.data === hoje;
                const diaFixo = isDiaFixo(dia.data);
                const diaExtra = getDiaExtra(dia.data);
                const agNoDia = getAgendamentosNoDia(dia.data);
                const temAtendimento = diaFixo || !!diaExtra;

                let bgColor = "white";
                let borderColor = theme.gray[200];

                if (dia.foraDoMes) {
                  bgColor = theme.gray[50];
                } else if (diaFixo && diaExtra) {
                  bgColor = "#fef3c7"; // Fixo + extra = amarelo
                  borderColor = "#f59e0b";
                } else if (diaFixo) {
                  bgColor = "#eff6ff"; // Fixo = azul claro
                  borderColor = "#3b82f6";
                } else if (diaExtra) {
                  bgColor = "#f0fdf4"; // Extra = verde claro
                  borderColor = "#22c55e";
                }

                if (isPassado && !dia.foraDoMes) {
                  bgColor = theme.gray[50];
                }

                return (
                  <div
                    key={i}
                    onClick={() =>
                      !dia.foraDoMes && !isPassado && toggleDia(dia.data)
                    }
                    style={{
                      padding: "6px",
                      minHeight: "65px",
                      background: bgColor,
                      border: `2px solid ${
                        isHoje ? theme.primary[500] : borderColor
                      }`,
                      borderRadius: "6px",
                      cursor:
                        dia.foraDoMes || isPassado ? "default" : "pointer",
                      opacity: dia.foraDoMes ? 0.3 : isPassado ? 0.5 : 1,
                      transition: "all 0.15s ease",
                    }}
                    onMouseOver={(e) => {
                      if (!dia.foraDoMes && !isPassado) {
                        e.currentTarget.style.transform = "scale(1.03)";
                        e.currentTarget.style.boxShadow = theme.shadows.sm;
                      }
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = "scale(1)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: isHoje ? "800" : "600",
                        color: isHoje ? theme.primary[700] : theme.gray[700],
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      {dia.dia}
                    </div>
                    {!dia.foraDoMes && temAtendimento && (
                      <div style={{ marginTop: "2px" }}>
                        {diaFixo && (
                          <div
                            style={{
                              fontSize: "10px",
                              color: "#3b82f6",
                              fontWeight: "700",
                              fontFamily: theme.fonts.sans,
                            }}
                          >
                            Fixo
                            {configSelecionado?.diasFixos
                              ?.filter(
                                (d) =>
                                  d.diaSemana ===
                                  new Date(dia.data + "T00:00:00").getDay()
                              )
                              .map((p, i) => (
                                <div
                                  key={i}
                                  style={{
                                    fontSize: "9px",
                                    fontWeight: "600",
                                    color: "#2563eb",
                                  }}
                                >
                                  {p.horaInicio}-{p.horaFim}
                                </div>
                              ))}
                          </div>
                        )}
                        {diaExtra && (
                          <div
                            style={{
                              fontSize: "10px",
                              color: "#16a34a",
                              fontWeight: "700",
                              fontFamily: theme.fonts.sans,
                            }}
                          >
                            Extra
                            <div style={{ fontSize: "9px", fontWeight: "600" }}>
                              {diaExtra.horaInicio}-{diaExtra.horaFim}
                            </div>
                          </div>
                        )}
                        {agNoDia.length > 0 && (
                          <div
                            style={{
                              marginTop: "2px",
                              fontSize: "10px",
                              fontWeight: "700",
                              color: "white",
                              background: theme.primary[500],
                              borderRadius: "6px",
                              textAlign: "center",
                              padding: "0 4px",
                              fontFamily: theme.fonts.sans,
                            }}
                          >
                            {agNoDia.length} pac.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* RESUMO DO MÊS */}
          <div
            style={{
              padding: "10px 12px",
              background: theme.gray[50],
              borderRadius: theme.borderRadius.md,
              fontSize: "12px",
              fontFamily: theme.fonts.sans,
              color: theme.gray[600],
              display: "flex",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            {configSelecionado?.tipoAgenda === "fixa" && (
              <span>
                Dias fixos:{" "}
                <strong>
                  {(configSelecionado.diasFixos || []).length} período(s)/semana
                </strong>
              </span>
            )}
            <span>
              Dias extras abertos: <strong>{diasExtrasDoMes.length}</strong>
            </span>
            <span>
              Duração consulta:{" "}
              <strong>{configSelecionado?.duracaoConsulta || 30} min</strong>
            </span>
          </div>
        </>
      )}

      {!profissionalId && (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.lg,
            color: theme.gray[400],
            fontFamily: theme.fonts.sans,
          }}
        >
          <Calendar
            size={40}
            color={theme.gray[300]}
            style={{ marginBottom: "8px" }}
          />
          <p>Selecione um profissional para programar os dias de atendimento</p>
        </div>
      )}
    </div>
  );
}
